import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ENDPOINTS } from "@/api/application-api";
import { ToastType } from "@/enums";
import { useDeleteWorkspaceInvite, useCreateWorkspaceMember } from "@/queries/workspace";
import { WorkspaceInvite } from "@/types/workspace-invite";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { URL_PARAM_KEYS } from "@/utils/utils";

export function useInviteActions(sameOrgInvites: WorkspaceInvite[]) {
  const createWorkspaceMember = useCreateWorkspaceMember({});
  const deleteWorkspaceInvite = useDeleteWorkspaceInvite({
    message: "Invitation declined successfully",
  });
  const [isBulkAccepting, setIsBulkAccepting] = useState(false);
  const [isBulkDeclining, setIsBulkDeclining] = useState(false);
  const queryClient = useQueryClient();

  const deleteInviteSearchParam = () => {
    sessionStorage.removeItem(URL_PARAM_KEYS.INVITE);
  };

  const handleAccept = (id: number) => {
    createWorkspaceMember.mutate({ workspace_invite_id: id });
    deleteInviteSearchParam();
  };

  const handleDecline = (id: number) => {
    deleteWorkspaceInvite.mutate(id);
    deleteInviteSearchParam();
  };

  const acceptAll = async () => {
    setIsBulkAccepting(true);
    let succeeded = 0;
    let failed = 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function handleSuccess(data: any) {
      if ("message" in data) {
        failed += 1;
      } else {
        succeeded += 1;
      }
    }
    for (const invite of sameOrgInvites) {
      try {
        await createWorkspaceMember.mutateAsync(
          {
            workspace_invite_id: invite.id,
          },
          {
            onSuccess: handleSuccess,
          },
        );
      } catch {
        failed += 1;
      }
    }
    const total = sameOrgInvites.length;

    if (succeeded === total) {
      displayToast(
        `You have successfully accepted all ${succeeded} invitations.`,
        ToastType.success,
      );
    } else if (succeeded > 0) {
      displayToast(
        `You have accepted ${succeeded} invitations, but ${failed} failed.`,
        ToastType.warning,
      );
    } else {
      displayErrorToast("Failed to accept all invitations.");
    }

    setIsBulkAccepting(false);
    queryClient.invalidateQueries([ENDPOINTS.workspace_invites]);
    queryClient.invalidateQueries([ENDPOINTS.organizations]);
    queryClient.invalidateQueries(["workspace-members"]);
    queryClient.invalidateQueries([ENDPOINTS.me]);
    deleteInviteSearchParam();
  };

  const declineAll = async () => {
    try {
      setIsBulkDeclining(true);
      const promises = sameOrgInvites.map(invite => deleteWorkspaceInvite.mutateAsync(invite.id));
      const results = await Promise.allSettled(promises);
      const fulfilledCount = results.filter(result => result.status === "fulfilled").length;
      const rejectedCount = results.length - fulfilledCount;
      if (fulfilledCount === results.length) {
        displayToast(
          `You have successfully declined ${fulfilledCount} invitations.`,
          ToastType.success,
        );
      } else if (fulfilledCount > 0) {
        displayToast(
          `You have declined ${fulfilledCount} invitations, but ${rejectedCount} failed.`,
          ToastType.warning,
        );
      } else {
        displayErrorToast("Failed to decline all invitations.");
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      displayErrorToast(error.message || "Failed to decline all invitations");
    } finally {
      setIsBulkDeclining(false);
      deleteInviteSearchParam();
    }
  };

  return {
    isAccepting: createWorkspaceMember.isLoading,
    isDeclining: deleteWorkspaceInvite.isLoading,
    handleAccept,
    handleDecline,
    acceptAll,
    declineAll,
    isBulkAccepting,
    isBulkDeclining,
  };
}
